/**
 * 共通 SaaS 認証インターセプター
 *
 * 全 API クライアント（billingApi / dashboardApi / accountApi / contactsApi /
 * projectsApi / apiKeysApi / usageApi 等）が使用する認証ロジックの単一の真実の源。
 *
 * ## 設計方針
 * - rtm_token（SaaS JWT）を Authorization に使う（auth_token より優先）
 * - auth_token は書き込み経路が存在しない legacy 遺物であり、リクエストごとに冪等に removeItem する
 * - 401 応答時:
 *   - auth_token は常に削除（冪等）
 *   - rtm_refresh_token が存在すれば refreshSaasToken() でトークンをリフレッシュし元リクエストをリトライする
 *   - リフレッシュ失敗 / リフレッシュトークン無し: `/auth/login?returnTo=<現在パス>` へリダイレクト
 *   - rtm_token が JWT として確実に期限切れ（exp < 現在時刻）の場合のみ rtm_token / rtm_refresh_token を削除
 *     （確実に期限切れと判定できる時だけ削除。有効なトークンを巻き添え削除しない原則を維持）
 *   - デコード不能・exp 無し・期限内の場合は rtm_token を保持
 *
 * ## 利用方法（推奨パターン）
 * ```ts
 * import { attachSaasAuth, buildSaasResponseErrorHandler } from './authInterceptors'
 *
 * const client = axios.create({ ... })
 * client.interceptors.request.use(attachSaasAuth, (error) => Promise.reject(error))
 * client.interceptors.response.use(
 *   (response) => response,
 *   buildSaasResponseErrorHandler(client),
 * )
 * ```
 *
 * ## sessionsApi の注意
 * sessionsApi は dev-token（rtm_session_token 経由）を Authorization に使い、
 * rtm_token は X-Saas-Authorization として co-send するため、このユーティリティを使わない。
 * SaaS JWT 期限切れによる UI への影響は useSessionManager の catch ブロックで対処する。
 *
 * ## fetch 用ヘルパー
 * axios 非依存の `getSaasAuthHeaders()` を使うと fetch 系呼び出しでも同じポリシーを適用できる。
 */

import { AxiosHeaders } from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios'

/**
 * _retried フラグを含む InternalAxiosRequestConfig の拡張型。
 * `as unknown as` キャストを排除するために定義する。
 */
interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retried?: boolean
}

/** localStorage キー定数 */
const KEY_LEGACY = 'auth_token'
const KEY_SAAS_JWT = 'rtm_token'
const KEY_REFRESH = 'rtm_refresh_token'
const KEY_DEV_TOKEN = 'rtm_session_token'

/** SaaS JWT リフレッシュエンドポイント（ループ防止のためのチェックに使用） */
const REFRESH_URL = '/api/auth/refresh'

// ========== JWT ユーティリティ ==========

/**
 * JWT が確実に期限切れか判定する。
 *
 * - payload を base64url デコードし exp クレーム（秒）を現在時刻と比較する
 * - デコード不能・exp 無し・期限内の場合は false を返す（保持）
 * - malformed は try/catch で握り期限切れ扱いにしない（保守的）
 */
export const isJwtExpired = (token: string): boolean => {
  try {
    const parts = token.split('.')
    if (parts.length < 3) return false
    // base64url → base64 変換（padding 補完）
    const payload = parts[1]
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padLength = (4 - (padded.length % 4)) % 4
    const base64 = padded + '='.repeat(padLength)
    const json = atob(base64)
    const parsed = JSON.parse(json) as Record<string, unknown>
    if (typeof parsed.exp !== 'number') return false
    return parsed.exp < Date.now() / 1000
  } catch {
    return false
  }
}

// ========== SaaS JWT リフレッシュ ==========

/**
 * SaaS JWT をリフレッシュする（シングルフライト実装）。
 *
 * - rtm_refresh_token が localStorage にある場合のみ BE の /api/auth/refresh を呼ぶ
 * - 成功時: 新しい access_token / refresh_token を localStorage に保存し access_token を返す
 * - 失敗時（401 / ネットワークエラー）: rtm_token / rtm_refresh_token をクリアして null を返す
 * - 並列に複数回呼ばれた場合: 1回目の Promise を共有して fetch は1度のみ実行する
 */
let _refreshPromise: Promise<string | null> | null = null

export const refreshSaasToken = (): Promise<string | null> => {
  if (_refreshPromise) {
    return _refreshPromise
  }

  const refreshToken = localStorage.getItem(KEY_REFRESH)
  if (!refreshToken) {
    return Promise.resolve(null)
  }

  _refreshPromise = (async (): Promise<string | null> => {
    try {
      // 他の API クライアントと同じ baseURL 解決ロジック（import.meta.env.VITE_API_BASE_URL）を使う。
      // window.__VITE_API_BASE_URL__ は存在しないプロパティであり、refresh だけ異なる
      // baseURL に POST される実バグを修正する。
      const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
      const response = await fetch(`${baseUrl}${REFRESH_URL}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })

      if (!response.ok) {
        // リフレッシュ失敗: 無効なセッションをクリア
        localStorage.removeItem(KEY_SAAS_JWT)
        localStorage.removeItem(KEY_REFRESH)
        return null
      }

      const data = (await response.json()) as {
        access_token: string
        refresh_token: string
        token_type: string
      }

      localStorage.setItem(KEY_SAAS_JWT, data.access_token)
      localStorage.setItem(KEY_REFRESH, data.refresh_token)
      return data.access_token
    } catch {
      // ネットワークエラーなど: 失敗として null を返す（トークンはそのまま保持）
      return null
    } finally {
      _refreshPromise = null
    }
  })()

  return _refreshPromise
}

/**
 * テスト用: シングルフライトキャッシュをリセットする
 * （テスト間で状態が残らないようにするため）
 */
export const _resetRefreshPromise = (): void => {
  _refreshPromise = null
}

// ========== ログインリダイレクト ==========

/**
 * ログイン画面へリダイレクトする内部ヘルパー。
 * 現在の URL を returnTo として付与する。
 */
const redirectToLogin = (): void => {
  if (typeof window === 'undefined') return
  const returnTo = encodeURIComponent(window.location.pathname)
  const loginUrl = `/auth/login${returnTo ? `?returnTo=${returnTo}` : ''}`
  window.location.assign(loginUrl)
}

/**
 * 認証失敗確定時にトークンを全クリアする内部ヘルパー。
 *
 * refresh が確定失敗した場合（リフレッシュトークン無し or リフレッシュ API 失敗）に呼ぶ。
 * この時点で rtm_token は確定的に無効なので、巻き添え削除リスクはない。
 * appStore（Zustand persist）は直接操作できないため、localStorage の
 * 'rtm-saas-storage' キーも消去してページロード時の自動ログアウトを誘発する。
 */
const clearAllTokensOnAuthFailure = (): void => {
  localStorage.removeItem(KEY_SAAS_JWT)
  localStorage.removeItem(KEY_REFRESH)
  localStorage.removeItem(KEY_DEV_TOKEN)
  localStorage.removeItem(KEY_LEGACY)
  // Zustand persist ストアのユーザー情報を消去してループを遮断する。
  // appStore を直接 import すると循環依存が生じるため、persist キーを直接操作する。
  try {
    const stored = localStorage.getItem('rtm-saas-storage')
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, unknown>
      const state = parsed.state as Record<string, unknown> | undefined
      if (state && 'user' in state) {
        state.user = null
        localStorage.setItem('rtm-saas-storage', JSON.stringify(parsed))
      }
    }
  } catch {
    // パース失敗時は全体を削除してクリーンな状態にする
    localStorage.removeItem('rtm-saas-storage')
  }
}

// ========== インターセプター ==========

/**
 * リクエストインターセプター: SaaS JWT を Authorization ヘッダーに設定する。
 *
 * - auth_token（レガシー）を冪等に removeItem する（書き込み経路は存在しない過去の遺物）
 * - rtm_token（SaaS JWT 正規キー）が存在すれば Authorization: Bearer <rtm_token> を設定する
 * - rtm_token が存在しない場合は Authorization ヘッダーを設定しない（未認証状態）
 */
export const attachSaasAuth = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  // legacy auth_token を移行ユーザー向けにクリア（冪等）
  localStorage.removeItem(KEY_LEGACY)

  const token = localStorage.getItem(KEY_SAAS_JWT)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
}

/**
 * SaaS JWT 401 レスポンスエラーハンドラーファクトリー（クライアントインスタンス版・推奨）。
 *
 * axios クライアントインスタンスを受け取り、そのインスタンスを使ってリトライを行うことで
 * 「ハンドラーだけ実装されていて実際のリトライが起きない」問題を構造的に排除する。
 *
 * ## 無限ループ防止
 * - `error.config._retried === true` の場合: すでにリトライ済みのため再試行せずエラーを throw
 * - リクエスト URL が `/api/auth/refresh` の場合: refresh エンドポイント自体の 401 のため
 *   再帰を防ぐためリフレッシュ試行をスキップしてリダイレクト
 *
 * ## 処理フロー（401 の場合）
 * 1. 上記ループ防止チェック → 該当すれば即エラー throw
 * 2. auth_token（legacy）削除 + 期限切れ rtm_token の削除（clearLegacyTokenOn401 の副作用を引き継ぐ）
 * 3. refreshSaasToken() でトークンリフレッシュを試みる
 * 4. リフレッシュ成功: Authorization ヘッダーを更新し、`_retried: true` フラグを付けて
 *    `client.request(config)` でリトライ → その結果を返す
 * 5. リフレッシュ失敗 / リフレッシュトークン無し: `/auth/login?returnTo=<現在パス>` へリダイレクト
 *
 * ## 401 以外
 * - そのまま throw する（上位の catch で apiError に変換すること）
 *
 * @param client - リトライに使用する axios クライアントインスタンス
 *
 * @example
 * ```ts
 * const client = axios.create({ baseURL })
 * client.interceptors.request.use(attachSaasAuth, (e) => Promise.reject(e))
 * client.interceptors.response.use(
 *   (res) => res,
 *   buildSaasResponseErrorHandler(client),
 * )
 * ```
 */
export const buildSaasResponseErrorHandler = (client: AxiosInstance) => {
  return async (error: AxiosError): Promise<never> => {
    if (error.response?.status !== 401) {
      // 401 以外はそのまま throw（apiError への変換は各クライアントの catch ブロックで行う）
      return Promise.reject(error)
    }

    const config = error.config as RetryableAxiosRequestConfig | undefined

    // 無限ループ防止 (1): すでにリトライ済み
    if (config?._retried === true) {
      return Promise.reject(error)
    }

    // 無限ループ防止 (2): refresh エンドポイント自体の 401（再帰回避）
    const requestUrl = config?.url ?? ''
    if (requestUrl.includes(REFRESH_URL)) {
      // refresh エンドポイント自体が 401 → セッション確定失敗。トークン全クリアしてリダイレクト
      clearAllTokensOnAuthFailure()
      redirectToLogin()
      return Promise.reject(error)
    }

    // legacy auth_token を常に削除（冪等）
    localStorage.removeItem(KEY_LEGACY)

    // SaaS JWT のリフレッシュを試みる（シングルフライト）。
    // 注意: rtm_token が期限切れでも rtm_refresh_token は削除しない。
    // refresh_token は refreshSaasToken() が失敗した場合にのみ内部で削除する。
    const newToken = await refreshSaasToken()

    if (newToken && config) {
      // リフレッシュ成功: Authorization ヘッダーを更新して _retried フラグ付きでリトライ
      // AxiosHeaders インスタンスを直接生成して型安全にヘッダーを構築する（as unknown as 不要）
      const retryHeaders = new AxiosHeaders(config.headers)
      retryHeaders.set('Authorization', `Bearer ${newToken}`)
      const retryConfig: RetryableAxiosRequestConfig = {
        ...config,
        _retried: true,
        headers: retryHeaders,
      }
      return client.request(retryConfig) as Promise<never>
    }

    // リフレッシュ失敗 or リフレッシュトークン無し: トークン全クリアしてログインへリダイレクト
    // この時点で rtm_token は確定的に無効（refresh 試行失敗）なので巻き添えリスクなし。
    clearAllTokensOnAuthFailure()
    redirectToLogin()
    return Promise.reject(error)
  }
}

/**
 * レスポンスエラーインターセプター: 401 時に legacy/期限切れトークンを削除する。
 *
 * @deprecated `buildSaasResponseErrorHandler(client)` を使用してください。
 *
 * clearLegacyTokenOn401 は副作用（トークン削除）のみを行い、リフレッシュ・リトライ・
 * リダイレクトは行いません。`buildSaasResponseErrorHandler` はこの副作用を内包しているため、
 * 新規クライアントでは clearLegacyTokenOn401 を個別に呼ぶ必要はありません。
 *
 * - auth_token は常に削除（冪等）
 * - rtm_token が JWT として確実に期限切れの場合のみ rtm_token / rtm_refresh_token を削除
 *   （クリーンなログアウト状態にして再ログインを促す）
 * - デコード不能・exp 無し・期限内の場合は rtm_token を保持
 *   （有効な SaaS JWT を巻き添え削除して強制ログアウトを引き起こすリスクを排除する）
 */
export const clearLegacyTokenOn401 = (error: AxiosError): Promise<never> => {
  if (error.response?.status === 401) {
    // legacy auth_token は常に削除（冪等）
    localStorage.removeItem(KEY_LEGACY)

    // rtm_token が確実に期限切れの場合のみ削除してクリーンなログアウト状態にする
    const rtmToken = localStorage.getItem(KEY_SAAS_JWT)
    if (rtmToken !== null && isJwtExpired(rtmToken)) {
      localStorage.removeItem(KEY_SAAS_JWT)
      localStorage.removeItem(KEY_REFRESH)
    }
  }
  return Promise.reject(error)
}

/**
 * @deprecated `buildSaasResponseErrorHandler(client)` を使用してください。
 *
 * SaaS JWT 401 エラーハンドラーファクトリー（旧・引数版）。
 * クライアントインスタンスを受け取らないため、実際のリトライは呼び出し側で手動実行が必要。
 * 後方互換のためエクスポートを維持するが、新規利用は buildSaasResponseErrorHandler を使うこと。
 *
 * @param originalRequest - リトライ時に再実行する関数（axios クライアントの呼び出し等）
 */
export const createSaasAuthErrorHandler = (
  originalRequest: () => Promise<unknown>
) => {
  return async (error: AxiosError): Promise<never> => {
    if (error.response?.status !== 401) {
      return Promise.reject(error)
    }

    // リフレッシュを試みる
    const newToken = await refreshSaasToken()

    if (newToken) {
      // リフレッシュ成功: 元リクエストをリトライ
      try {
        await originalRequest()
        return undefined as never
      } catch (retryError) {
        return Promise.reject(retryError)
      }
    }

    // リフレッシュ失敗 or リフレッシュトークンなし: ログインへリダイレクト
    redirectToLogin()
    return Promise.reject(error)
  }
}

// ========== fetch 用ヘルパー ==========

/**
 * fetch 用認証ヘルパー: SaaS JWT を Authorization ヘッダーとして返す。
 *
 * axios インターセプターと同じポリシーを fetch 系呼び出しに適用する:
 * - auth_token（レガシー）を冪等に removeItem する
 * - rtm_token が存在すれば `{ Authorization: 'Bearer <rtm_token>' }` を返す
 * - rtm_token が存在しない場合は空オブジェクトを返す（未認証状態）
 *
 * @example
 * const response = await fetch(url, { headers: getSaasAuthHeaders() })
 */
export const getSaasAuthHeaders = (): Record<string, string> => {
  // legacy auth_token を移行ユーザー向けにクリア（冪等）
  localStorage.removeItem(KEY_LEGACY)

  const token = localStorage.getItem(KEY_SAAS_JWT)
  if (token) {
    return { Authorization: `Bearer ${token}` }
  }
  return {}
}
